import { Box, Typography, Tooltip } from '@mui/material';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { useTheme } from '@mui/material/styles';
import { useDatastore } from '../../hooks';

interface FullSyncIndicatorProps {
  compact?: boolean;
}

export default function FullSyncIndicator({ compact = false }: FullSyncIndicatorProps) {
  const theme = useTheme();
  const { isDatastoreReady, isSyncing } = useDatastore();

  // Show only for background syncs after the app is already usable.
  if (!isDatastoreReady || !isSyncing) {
    return null;
  }

  return (
    <Tooltip
      enterTouchDelay={0}
      title={
        <Box sx={{ p: 1 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5, color: 'white' }}>
            Full Sync In Progress
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.9)', lineHeight: 1.4 }}>
            DataStore is running a background full sync. Actions are queued and will be uploaded automatically when sync
            completes.
          </Typography>
        </Box>
      }
      arrow
      placement="bottom"
      componentsProps={{
        tooltip: {
          sx: {
            bgcolor: 'rgba(0, 0, 0, 0.9)',
            maxWidth: 320,
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
            border: '1px solid rgba(255, 255, 255, 0.1)'
          }
        },
        arrow: {
          sx: {
            color: 'rgba(0, 0, 0, 0.9)'
          }
        }
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: compact ? 0.5 : 1,
          px: compact ? 0.8 : 2,
          py: compact ? 0.75 : 0.75,
          minHeight: 36,
          boxSizing: 'border-box',
          borderRadius: 2,
          backgroundColor: 'rgba(255, 193, 7, 0.14)',
          border: '1px solid rgba(255, 193, 7, 0.35)'
        }}>
        <Typography
          variant="body2"
          sx={{
            fontSize: compact ? '0.75rem' : '0.875rem',
            fontWeight: 600,
            color: theme.palette.primary.contrastText
          }}>
          {compact ? 'Syncing' : 'Full sync in progress'}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            animation: 'spin 1.3s linear infinite',
            '@keyframes spin': {
              from: { transform: 'rotate(0deg)' },
              to: { transform: 'rotate(360deg)' }
            }
          }}>
          <AutorenewIcon sx={{ fontSize: 16, color: '#FFB300' }} />
        </Box>
      </Box>
    </Tooltip>
  );
}
